import { Link, NavLink, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useAuth } from "@/auth";
import { useLang } from "@/i18n";
import { trackEvent } from "@/api";
import { LogOut, KeyRound, LayoutDashboard, Users, Dumbbell, Apple, Pill, BadgePercent, StretchHorizontal, Sun, Moon } from "lucide-react";
import { useTheme } from "@/theme";

export default function Layout({ nav, tabs, children, dark = false }) {
  const { user, logout } = useAuth();
  const { lang, setLang, t } = useLang();
  const navigate = useNavigate();
  const [theme, setTheme] = useTheme();
  const isDark = theme === null ? dark : theme === "dark";

  useEffect(() => { if (user) trackEvent("app_open"); }, [user?._id]);

  const coachTabs = user?.role === "coach" && !tabs ? [
    { to: "/coach", key: "coach-tab-dashboard", icon: LayoutDashboard, label: t("nav.dashboard"), end: true },
    { to: "/coach/clients", key: "coach-tab-clients", icon: Users, label: t("nav.clients") },
    { to: "/coach/programs", key: "coach-tab-programs", icon: Dumbbell, label: t("nav.programs") },
    { to: "/coach/nutrition", key: "coach-tab-nutrition", icon: Apple, label: t("nav.nutrition") },
    { to: "/coach/supplements", key: "coach-tab-supplements", icon: Pill, label: t("nav.supplements") },
    { to: "/coach/stretching", key: "coach-tab-stretching", icon: StretchHorizontal, label: t("nav.stretching") },
    { to: "/coach/promos", key: "coach-tab-promos", icon: BadgePercent, label: t("nav.promos") },
  ] : null;
  const effectiveTabs = tabs || coachTabs;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const navLink = (item, prefix = "nav-link") => (
    <NavLink
      key={item.to}
      to={item.to}
      end={item.end}
      data-testid={`${prefix}-${item.key}`}
      className={({ isActive }) =>
        `px-3 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors ${
          isActive ? "bg-primary/15 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
        }`
      }
    >
      {item.label}
    </NavLink>
  );

  return (
    <div className={`min-h-screen ${isDark ? "kc-theme-dark" : ""}`}>
      <div aria-hidden="true" data-testid="client-app-bg" className="fixed inset-0 -z-10 overflow-hidden" style={{ background: isDark ? "#141213" : "#FDFBFB" }}>
        <img src="/hero-training-back.webp" alt="" className={`w-full h-full object-cover object-[center_25%] ${isDark ? "" : "opacity-[0.16]"}`} />
        <div className="absolute inset-0" style={{ background: isDark
          ? "linear-gradient(180deg, rgba(43,33,38,0.80) 0%, rgba(43,33,38,0.86) 45%, rgba(43,33,38,0.93) 100%)"
          : "linear-gradient(180deg, rgba(253,251,251,0.80) 0%, rgba(253,251,251,0.87) 45%, rgba(253,251,251,0.94) 100%)" }} />
      </div>
      <header className={`sticky top-0 z-50 backdrop-blur-xl border-b ${isDark ? "bg-[#141213]/85 border-[#453740]" : "bg-background/90 border-border"}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center gap-4 h-16">
          <Link to="/" data-testid="nav-brand" className="flex items-center">
            <img src="/adtraining-h.png" alt="AD TRAINING PROGRAM" className="h-9 w-auto kc-logo-ondark" />
            <img src="/adtraining-h-onlight.png" alt="AD TRAINING PROGRAM" className="h-9 w-auto kc-logo-onlight" />
          </Link>
          <nav className="flex-1 hidden md:flex items-center gap-1 overflow-x-auto">
            {nav.map((item) => navLink(item))}
          </nav>
          <div className="flex items-center gap-2">
            <div className="flex rounded-lg border border-border overflow-hidden" data-testid="lang-switcher">
              {["it", "en"].map((l) => (
                <button
                  key={l}
                  data-testid={`lang-${l}-button`}
                  onClick={() => setLang(l)}
                  className={`px-2.5 py-1.5 text-xs font-bold uppercase transition-colors ${
                    lang === l ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
            <button data-testid="theme-toggle-button" onClick={() => setTheme(isDark ? "light" : "dark")}
              className="kc-btn-ghost !px-2.5" title={t("theme.toggle")}>
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <span className="hidden md:block text-sm text-muted-foreground">{user?.name}</span>
            <Link to="/change-password" data-testid="change-password-link" className="kc-btn-ghost !px-2.5" title={t("pw.title")}>
              <KeyRound className="w-5 h-5" />
            </Link>
            <button data-testid="logout-button" onClick={handleLogout} className="kc-btn-ghost !px-2.5" title={t("common.logout")}>
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
        <nav data-testid="mobile-nav" className={`md:hidden flex items-center gap-1 overflow-x-auto px-3 py-1.5 border-t ${isDark ? "border-[#453740]" : "border-border"}`}>
          {nav.map((item) => navLink(item, "mnav-link"))}
        </nav>
      </header>
      <main className={`max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10 ${effectiveTabs ? "pb-28" : ""}`}>{children}</main>
      {effectiveTabs && (
        <nav className={`fixed bottom-0 inset-x-0 z-50 backdrop-blur-xl border-t ${isDark ? "border-[#453740]" : "border-border"}`} style={isDark ? { background: "rgba(43,33,38,0.95)" } : { background: "hsl(var(--background) / 0.95)" }} data-testid="client-bottom-nav">
          <div className="max-w-lg mx-auto flex justify-around gap-1 px-2 py-1.5 overflow-x-auto">
            {effectiveTabs.map((tab) => (
              <NavLink key={tab.key} to={tab.to} end={tab.end} data-testid={tab.key}
                className={({ isActive }) =>
                  `flex flex-col items-center gap-0.5 px-1.5 py-1.5 rounded-xl text-[10px] font-semibold whitespace-nowrap min-w-0 transition-colors ${
                    isActive ? (isDark ? "text-[#E0597F]" : "text-primary") : (isDark ? "text-[#F5E6E8]/55 hover:text-[#F5E6E8]" : "text-muted-foreground hover:text-foreground")
                  }`
                }>
                <tab.icon className="w-5 h-5" />
                {tab.label}
              </NavLink>
            ))}
          </div>
        </nav>
      )}
    </div>
  );
}
